import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../hooks/useAuth";
import { useLanguage } from "../contexts/LanguageContext";
import Sidebar from "../components/Sidebar";
import QRCode from "qrcode";
import {
  automationMonitoring,
  securityCompliance,
  workerQueue,
  aiGateway,
} from "../services";

// SVG Icon Components
const QRCodeIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1v-2a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
    />
  </svg>
);

const UploadIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
    />
  </svg>
);

const LocationIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

const CheckIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

interface PromptPayData {
  amount: number;
  reference: string;
  qrCode: string;
  expiresAt: Date;
}

const thaiBanks = [
  { code: "BBL", name: "Bangkok Bank", color: "#003366" },
  { code: "KTB", name: "Krung Thai Bank", color: "#006633" },
  { code: "KBank", name: "Kasikorn Bank", color: "#009944" },
  { code: "SCB", name: "Siam Commercial Bank", color: "#660066" },
  { code: "GSB", name: "Government Savings Bank", color: "#CC0000" },
  { code: "Krungsri", name: "Bank of Ayudhya", color: "#FF6600" },
  { code: "CIMB", name: "CIMB Thai Bank", color: "#FF0000" },
  { code: "TTB", name: "TMB Thanachart Bank", color: "#FF9900" },
  { code: "UOB", name: "United Overseas Bank", color: "#FF3399" },
  { code: "SC", name: "Standard Chartered", color: "#006699" },
];

export default function PromptPay() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [promptPayId, setPromptPayId] = useState("0840871065");
  const [selectedBank, setSelectedBank] = useState(thaiBanks[0]);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [generated, setGenerated] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<
    "pending" | "verified" | "failed"
  >("pending");
  const [geoLocation, setGeoLocation] = useState<{
    country: string;
    city: string;
    lat: number;
    lon: number;
  } | null>(null);
  const [geoConsent, setGeoConsent] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrConfidence, setOcrConfidence] = useState(0);
  const [processingTime, setProcessingTime] = useState(0);
  const [ocrStatus, setOcrStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [aiExtraction, setAiExtraction] = useState<any>(null);
  const [paymentRouting, setPaymentRouting] = useState<any>(null);
  const [fraudDetection, setFraudDetection] = useState<any>(null);

  // Worker refs for proper cleanup
  const workerRef = useRef<Worker | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Proper worker cleanup on unmount
  useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const generateQRCode = async () => {
    // PromptPay QR code generation using phone number
    // Format follows Thai PromptPay standard (EMVCo QR)
    const formattedPhone = promptPayId.replace(/^0/, "66"); // Convert 084... to 6684...
    const amountValue = parseFloat(amount) || 0;
    const amountSatang = Math.round(amountValue * 100); // Convert to satang

    // Build PromptPay QR data according to Thai standard
    // 00: Payload Format Indicator
    // 01: Point of Initiation Method (Static/Dynamic)
    // 52: Merchant Account Information (PromptPay ID)
    // 53: Transaction Currency (764 = THB)
    // 54: Transaction Amount
    // 58: Merchant Category Code
    // 59: Merchant City
    // 60: Merchant Name
    // 62: Additional Data (Bill number)
    // 63: CRC (Checksum)

    let promptPayData = "000201"; // Payload Format Indicator
    promptPayData += "010211"; // Point of Initiation Method (Dynamic)

    // Merchant Account Information (PromptPay ID)
    promptPayData += "29"; // Length of Merchant Account Information
    promptPayData += "0016"; // Global Unique Identifier
    promptPayData += "00"; // Payment Network (00 = PromptPay)
    promptPayData += formattedPhone; // Phone number

    // Transaction Currency (764 = THB)
    promptPayData += "5303764";

    // Transaction Amount (if provided)
    if (amountValue > 0) {
      const amountStr = amountSatang.toString().padStart(10, "0");
      promptPayData += "54" + amountStr.length.toString().padStart(2, "0") + amountStr;
    }

    // Country Code (TH)
    promptPayData += "5802TH";

    // Merchant Name (Edge Commerce)
    promptPayData += "5913Edge Commerce";

    // Merchant City (Bangkok)
    promptPayData += "6007Bangkok";

    // Additional Data (Reference number if provided)
    if (reference) {
      promptPayData += "62" + (reference.length + 3).toString().padStart(2, "0");
      promptPayData += "01"; // Bill Number tag
      promptPayData += reference.length.toString().padStart(2, "0") + reference;
    }

    // Calculate CRC checksum
    const crc = calculateCRC(promptPayData + "6304");
    promptPayData += "6304" + crc;

    try {
      // Generate actual QR code image
      const qrCodeDataUrl = await QRCode.toDataURL(promptPayData, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });
      setQrCode(qrCodeDataUrl);
      setGenerated(true);
      setVerificationStatus("pending");
    } catch (error) {
      console.error('QR Code generation error:', error);
      setError('Failed to generate QR code');
    }
  };

  // Simple CRC-16 calculation for EMVCo QR
  const calculateCRC = (data: string): string => {
    let crc = 0xFFFF;
    for (let i = 0; i < data.length; i++) {
      crc ^= data.charCodeAt(i) << 8;
      for (let j = 0; j < 8; j++) {
        if (crc & 0x8000) {
          crc = (crc << 1) ^ 0x1021;
        } else {
          crc = crc << 1;
        }
        crc &= 0xFFFF;
      }
    }
    return crc.toString(16).toUpperCase().padStart(4, "0");
  };

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        // File validation
        const maxSize = 10 * 1024 * 1024; // 10MB
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

        if (file.size > maxSize) {
          setError('File size must be less than 10MB');
          return;
        }

        if (!allowedTypes.includes(file.type)) {
          setError('Only JPEG, PNG, and WebP images are allowed');
          return;
        }

        // Additional validation for image dimensions
        const img = new Image();
        img.onload = () => {
          if (img.width < 200 || img.height < 200) {
            setError('Image is too small. Minimum size is 200x200 pixels.');
            return;
          }
          if (img.width > 4000 || img.height > 4000) {
            setError('Image is too large. Maximum size is 4000x4000 pixels.');
            return;
          }

          // If validation passes, proceed with OCR
          setUploadedFile(file);
          setVerificationStatus("pending");
          setOcrProgress(0);
          setOcrStatus("processing");

          // Start OCR processing inline
          const startTime = Date.now();
          let timedOut = false;

          // Log audit event for compliance
          securityCompliance.logAuditEvent({
            action: "OCR_PROCESSING_START",
            resource: "payment_verification",
            outcome: "success",
            details: { fileName: file.name, fileSize: file.size },
            dataClassification: "confidential",
            retentionDays: 365,
          });

          // Use enhanced OCR worker with confidence scoring
          workerRef.current = new Worker(
            new URL("../workers/ocr-worker.ts", import.meta.url),
            { type: "module" },
          );

          // Add timeout mechanism
          const timeoutMs = 60000; // 60 seconds timeout
          timeoutRef.current = setTimeout(() => {
            timedOut = true;
            if (workerRef.current) {
              workerRef.current.terminate();
              workerRef.current = null;
            }
            setOcrStatus("error");
            setError("OCR processing timed out. Please try again.");
          }, timeoutMs);

          // Add error handler
          workerRef.current.onerror = (error) => {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            setOcrStatus("error");
            setError("OCR processing failed: " + error.message);
            console.error("Worker error:", error);
          };

          workerRef.current.postMessage({
            file,
            languages: "eng+tha",
            retryConfig: {
              maxRetries: 3,
              confidenceThreshold: 70,
              backoffMs: 1000,
            },
          });

          workerRef.current.onmessage = async (event) => {
            const { type, data, progress, retryCount, message, error } =
              event.data;

            if (type === "progress") {
              setOcrProgress(progress * 100);
              if (message) console.log("OCR:", message);
            } else if (type === "success") {
              const endTime = Date.now();
              const totalProcessingTime = endTime - startTime;
              setProcessingTime(totalProcessingTime);
              setOcrConfidence(data.confidence);

              console.log("Enhanced OCR Result:", data);

              // Extract payment details using OCR results
              let extractedAmount = extractAmount(data.rawText || "");
              let extractedPhone = extractPhoneNumber(data.rawText || "");
              let extractedReference = extractReference(data.rawText || "");

              console.log("Final Extracted Data:", {
                extractedAmount,
                extractedPhone,
                extractedReference,
              });

              // Record OCR metrics
              automationMonitoring.recordOCRExtraction(
                data.confidence,
                data.processingTime,
                {
                  amount: extractedAmount,
                  phone: extractedPhone,
                  reference: extractedReference,
                },
                {
                  amount: parseFloat(amount || "0"),
                  phone: promptPayId,
                  reference,
                },
                "eng+tha",
              );

              // Add verification task to queue
              const taskId = workerQueue.addTask({
                type: "verification",
                data: {
                  extractedData: {
                    amount: extractedAmount,
                    phone: extractedPhone,
                    reference: extractedReference,
                  },
                  expectedData: {
                    amount: parseFloat(amount || "0"),
                    phone: promptPayId,
                    reference,
                  },
                },
                priority: "high",
                maxRetries: 2,
              });

              console.log(`Verification task queued: ${taskId}`);

              // AI payment routing
              try {
                const routingResult = await aiGateway.routePayment(
                  extractedAmount,
                  "THB",
                  extractedPhone,
                  "promptpay",
                );
                setPaymentRouting(routingResult);
                console.log("AI Payment Routing:", routingResult);
              } catch (routingError) {
                console.warn("Payment routing failed:", routingError);
              }

              // AI fraud detection
              try {
                const fraudResult = await aiGateway.detectFraud(
                  data.rawText || "",
                  "payment-fraud",
                );
                setFraudDetection(fraudResult);
                console.log("AI Fraud Detection:", fraudResult);

                // Log fraud detection results
                securityCompliance.logAuditEvent({
                  action: "FRAUD_DETECTION_PERFORMED",
                  resource: "payment_verification",
                  outcome: fraudResult.success ? "success" : "failure",
                  details: {
                    riskScore: fraudResult.riskScore,
                    riskLevel: fraudResult.riskLevel,
                    patternsFound: fraudResult.data.totalFound,
                  },
                  dataClassification: "confidential",
                  retentionDays: 2555,
                });
              } catch (fraudError) {
                console.warn("Fraud detection failed:", fraudError);
              }

              // Verify the extracted data matches expected values
              const amountMatch =
                Math.abs(extractedAmount - parseFloat(amount || "0")) < 0.01;
              const phoneMatch =
                extractedPhone === promptPayId ||
                extractedPhone === promptPayId.replace(/^0/, "66");
              const referenceMatch =
                !reference || extractedReference === reference;

              if (amountMatch && phoneMatch && referenceMatch) {
                setVerificationStatus("verified");

                // Log successful verification
                securityCompliance.logAuditEvent({
                  action: "PAYMENT_VERIFICATION_SUCCESS",
                  resource: "promptpay_verification",
                  outcome: "success",
                  details: {
                    confidence: data.confidence,
                    processingTime: totalProcessingTime,
                    retryCount: data.retryCount,
                    aiEnhanced: !!aiExtraction,
                    paymentRouting: !!paymentRouting,
                    fraudRiskLevel: fraudDetection?.riskLevel,
                  },
                  dataClassification: "confidential",
                  retentionDays: 2555,
                });
              } else {
                setVerificationStatus("failed");

                // Log failed verification
                securityCompliance.logAuditEvent({
                  action: "PAYMENT_VERIFICATION_FAILED",
                  resource: "promptpay_verification",
                  outcome: "failure",
                  details: {
                    amountMatch,
                    phoneMatch,
                    referenceMatch,
                    extractedData: {
                      amount: extractedAmount,
                      phone: extractedPhone,
                      reference: extractedReference,
                    },
                    aiEnhanced: !!aiExtraction,
                    paymentRouting: !!paymentRouting,
                    fraudRiskLevel: fraudDetection?.riskLevel,
                  },
                  dataClassification: "confidential",
                  retentionDays: 2555,
                });
              }

              worker.terminate();
            } else if (type === "error") {
              console.error("Enhanced OCR Error:", error);
              setVerificationStatus("failed");

              // Record OCR failure
              automationMonitoring.recordOCRExtraction(
                0,
                Date.now() - startTime,
                {},
                {
                  amount: parseFloat(amount || "0"),
                  phone: promptPayId,
                  reference,
                },
                "eng+tha",
                error,
              );

              // Clean up worker
              if (workerRef.current) {
                workerRef.current.terminate();
                workerRef.current = null;
              }
              setOcrStatus("error");
            }
          };
        };

        img.onerror = () => {
          setError('Invalid image file. Please upload a valid image.');
        };

        img.src = URL.createObjectURL(file);
      }
    },
    []
  );

  const extractAmount = (text: string): number => {
    // Extract amount from OCR text (Thai baht format)
    const amountPattern = /฿?\s*([\d,]+\.?\d*)|(\d+\.?\d*)\s*฿/gi;
    const match = text.match(amountPattern);
    if (match) {
      const amount = match[0].replace(/[฿,]/g, "");
      return parseFloat(amount) || 0;
    }
    return 0;
  };

  const extractPhoneNumber = (text: string): string => {
    // Extract phone number from OCR text
    const phonePattern = /0\d{8,9}|66\d{8,9}/g;
    const match = text.match(phonePattern);
    return match ? match[0] : "";
  };

  const extractReference = (text: string): string => {
    // Extract reference number from OCR text
    const refPattern = /REF|Reference|ref[:\s]*([A-Z0-9-]+)/gi;
    const match = text.match(refPattern);
    if (match) {
      const refMatch = match[0].match(/([A-Z0-9-]+)/);
      return refMatch ? refMatch[0] : "";
    }
    return "";
  };

  const handleGeoConsent = useCallback(async () => {
    if (geoConsent) {
      // Log consent for compliance
      securityCompliance.logAuditEvent({
        action: "GEOLOCATION_CONSENT_GIVEN",
        resource: "location_verification",
        outcome: "success",
        details: { consent: true, timestamp: new Date().toISOString() },
        dataClassification: "personalInfo",
        retentionDays: 1095,
      });

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            const location = {
              country: "Thailand",
              city: "Bangkok",
              lat: latitude,
              lon: longitude,
            };

            // Validate regional compliance
            const compliance = securityCompliance.validateRegionalCompliance(
              location,
              "payment_verification",
            );

            if (!compliance.compliant) {
              console.warn(
                "Regional compliance issues:",
                compliance.restrictions,
              );
            }

            setGeoLocation(location);

            // Log location data
            securityCompliance.logAuditEvent({
              action: "LOCATION_DETECTED",
              resource: "geo_verification",
              outcome: "success",
              details: {
                country: location.country,
                city: location.city,
                compliance: compliance.compliant,
              },
              dataClassification: "personalInfo",
              retentionDays: 1095,
            });
          },
          (error) => {
            console.error("Geolocation error:", error);
            const fallbackLocation = {
              country: "Thailand",
              city: "Bangkok",
              lat: 13.7563,
              lon: 100.5018,
            };

            setGeoLocation(fallbackLocation);

            // Log fallback location
            securityCompliance.logAuditEvent({
              action: "LOCATION_FALLBACK",
              resource: "geo_verification",
              outcome: "partial",
              details: {
                error: error.message,
                fallbackLocation,
              },
              dataClassification: "personalInfo",
              retentionDays: 1095,
            });
          },
        );
      } else {
        const noGeoLocation = {
          country: "Thailand",
          city: "Bangkok",
          lat: 13.7563,
          lon: 100.5018,
        };

        setGeoLocation(noGeoLocation);

        securityCompliance.logAuditEvent({
          action: "GEOLOCATION_UNSUPPORTED",
          resource: "geo_verification",
          outcome: "partial",
          details: { fallbackLocation: noGeoLocation },
          dataClassification: "personalInfo",
          retentionDays: 1095,
        });
      }
    } else {
      setGeoLocation(null);

      // Log consent withdrawal
      securityCompliance.logAuditEvent({
        action: "GEOLOCATION_CONSENT_WITHDRAWN",
        resource: "location_verification",
        outcome: "success",
        details: { consent: false, timestamp: new Date().toISOString() },
        dataClassification: "personalInfo",
        retentionDays: 1095,
      });
    }
  }, [geoConsent]);

  return (
    <div className="flex min-h-screen bg-white">
      <Sidebar />
      <div className="flex-1">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">PromptPay</h1>
              <p className="text-sm font-medium text-gray-600 mt-1">
                Thai payment QR code generation and verification
              </p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="font-mono text-2xl font-bold text-gray-900 tracking-wide">
                  {currentTime.toLocaleTimeString("en-GB", { hour12: false })}
                </div>
                <div className="text-sm font-medium text-gray-600">
                  {currentTime.toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* PromptPay Generator */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* QR Code Generation */}
            <div className="card bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2 tracking-tight">
                <QRCodeIcon />
                Generate PromptPay QR Code
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Select Bank
                  </label>
                  <select
                    value={selectedBank.code}
                    onChange={(e) => {
                      const bank = thaiBanks.find(
                        (b) => b.code === e.target.value,
                      );
                      if (bank) setSelectedBank(bank);
                    }}
                    className="w-full px-3 py-2 border border-blue-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-md"
                  >
                    {thaiBanks.map((bank) => (
                      <option key={bank.code} value={bank.code}>
                        {bank.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    PromptPay Phone Number
                  </label>
                  <input
                    type="tel"
                    value={promptPayId}
                    onChange={(e) => setPromptPayId(e.target.value)}
                    placeholder="08xxxxxxxx"
                    className="w-full px-3 py-2 border border-blue-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-md"
                  />
                  <p className="text-xs font-medium text-gray-500 mt-1">
                    Thai mobile number for PromptPay
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Amount (฿)
                  </label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="w-full px-3 py-2 border border-blue-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Reference Number
                  </label>
                  <input
                    type="text"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="Enter reference"
                    className="w-full px-3 py-2 border border-blue-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-md"
                  />
                </div>

                <button
                  onClick={generateQRCode}
                  disabled={!amount || !promptPayId}
                  className="w-full bg-blue-600 text-white font-semibold px-4 py-3 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm tracking-wide"
                >
                  Generate QR Code
                </button>
              </div>

              {generated && qrCode && (
                <div className="mt-6 p-4 bg-white border border-gray-200 rounded-xl">
                  <div className="text-center">
                    <div
                      className="w-48 h-48 mx-auto bg-white flex items-center justify-center mb-4"
                      style={{ border: `3px solid ${selectedBank.color}` }}
                    >
                      <img
                        src={qrCode}
                        alt="PromptPay QR Code"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="text-sm text-gray-700">
                      <p
                        className="font-bold text-base"
                        style={{ color: selectedBank.color }}
                      >
                        {selectedBank.name}
                      </p>
                      <p className="font-semibold mt-1">PromptPay: {promptPayId}</p>
                      <p className="font-semibold">Amount: ฿{amount}</p>
                      {reference && <p className="font-medium">Reference: {reference}</p>}
                      <p className="text-xs font-medium text-gray-500 mt-2">
                        Scan with PromptPay app
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Payment Verification */}
            <div className="card bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2 tracking-tight">
                <UploadIcon />
                Payment Verification
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Upload Payment Screenshot
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <UploadIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-sm font-medium text-gray-700">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs font-medium text-gray-500 mt-1">
                        PNG, JPG up to 10MB
                      </p>
                    </label>
                  </div>
                </div>

                {uploadedFile && (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-blue-800">
                          {uploadedFile.name}
                        </span>
                        <span className="text-xs font-medium text-blue-600">
                          ({(uploadedFile.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      {verificationStatus === "verified" && (
                        <CheckIcon className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  </div>
                )}

                {verificationStatus === "verified" && (
                  <div className="p-3 bg-green-500 border border-green-200 rounded-xl">
                    <p className="text-sm text-white font-bold">
                      ✓ Payment verified successfully
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* GeoIP Verification */}
          <div className="card bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow mt-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2 tracking-tight">
              <LocationIcon />
              GeoIP Location Verification
            </h3>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="geo-consent"
                  checked={geoConsent}
                  onChange={(e) => {
                    setGeoConsent(e.target.checked);
                    handleGeoConsent();
                  }}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="geo-consent" className="text-sm font-medium text-gray-800">
                  I agree to share my location for payment verification
                </label>
              </div>

              {geoLocation && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                  <div className="flex items-center gap-2">
                    <LocationIcon className="w-5 h-5 text-blue-600" />
                    <div className="flex-1">
                      <p className="text-sm font-bold text-blue-900">
                        Location Detected
                      </p>
                      <p className="text-xs font-semibold text-blue-700">
                        {geoLocation.city}, {geoLocation.country}
                      </p>
                      <p className="text-xs font-light text-blue-600 mt-1">
                        Lat: {geoLocation.lat.toFixed(6)}, Lon: {" "}
                        {geoLocation.lon.toFixed(6)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recent Transactions */}
          <div className="card bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow mt-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 tracking-tight">
              Recent PromptPay Transactions
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl">
                <div>
                  <p className="text-sm font-bold text-gray-900">฿1,250.00</p>
                  <p className="text-xs font-medium text-gray-600">REF: PP-2024-001</p>
                </div>
                <div className="text-right">
                  <span className="px-2 py-1 text-xs font-bold rounded-full bg-blue-500 text-white">
                    Verified
                  </span>
                  <p className="text-xs font-medium text-gray-500 mt-1">2 min ago</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-gray-900">฿3,500.00</p>
                  <p className="text-xs text-gray-500">REF: PP-2024-002</p>
                </div>
                <div className="text-right">
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-500 text-white">
                    Pending
                  </span>
                  <p className="text-xs text-gray-400 mt-1">15 min ago</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-gray-900">฿890.00</p>
                  <p className="text-xs text-gray-500">REF: PP-2024-003</p>
                </div>
                <div className="text-right">
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-500 text-white">
                    Verified
                  </span>
                  <p className="text-xs text-gray-400 mt-1">1 hour ago</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
